import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';

import { useTransferFiles } from '@/hooks';
import { DEFAULT_FILE_MANAGER_CONFIG } from '@/constants/FileManagerConstants';
import {
  FileList,
  FileNavigator,
  UploadConfirmDialog,
} from '@/components/FileManager';
import { FileManagerErrorBoundary } from '@/components/ErrorBoundary';

/**
 * Admin page for managing files in S3. Handles upload, download,
 * delete (archive), and folder navigation via the useTransferFiles hook.
 */
const FileManagerPage: React.FC = () => {
  const {
    files,
    folders,
    currentPath,
    loading,
    error,
    uploadingFile,
    uploadProgress,
    uploadMessage,
    loadFiles,
    handleUpload,
    handleDelete,
    handleDownload,
    navigateToFolder,
  } = useTransferFiles(DEFAULT_FILE_MANAGER_CONFIG);

  // Local state for upload confirmation dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // File input ref for upload
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load files once on mount
  useEffect(() => {
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset errors
  const resetErrors = () => {
    loadFiles();
  };

  const handleNavigate = (path: string) => {
    loadFiles(path);
  };

  // Handle file selection for upload - show confirmation dialog
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadDialogOpen(true);
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle upload confirmation
  const handleUploadConfirm = (file: File, destinationPath: string) => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    handleUpload(file, destinationPath);
  };

  // Handle upload cancellation
  const handleUploadCancel = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
  };

  // Trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <FileManagerErrorBoundary onReset={resetErrors}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h2" gutterBottom fontWeight={400}>
          File Manager
        </Typography>

        <Paper elevation={1} sx={{ p: 3 }}>
          {/* Navigation + Upload row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <FileNavigator
                currentPath={currentPath}
                onNavigate={handleNavigate}
                basePath={DEFAULT_FILE_MANAGER_CONFIG.basePath}
                loading={loading}
                error={error || undefined}
              />
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              disabled={uploadingFile !== null}
            />
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadClick}
              disabled={uploadingFile !== null}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Upload File
            </Button>
          </Box>

          {/* Upload Progress */}
          {uploadingFile && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant={
                  uploadProgress >= 100 ? 'indeterminate' : 'determinate'
                }
                value={uploadProgress}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary" align="center">
                {uploadProgress >= 100
                  ? `Finalizing ${uploadingFile.name}...`
                  : `Uploading ${uploadingFile.name}... ${uploadProgress}%`}
              </Typography>
            </Box>
          )}

          {/* Upload Success Message */}
          {uploadMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {uploadMessage}
            </Alert>
          )}

          {/* File List Component */}
          <FileList
            files={files}
            folders={folders}
            onDownload={file => handleDownload(file.key)}
            onDelete={file => handleDelete(file.key)}
            onFolderClick={navigateToFolder}
            loading={loading}
            loadingFile={uploadingFile?.name}
          />

          {/* Upload Confirmation Dialog */}
          <UploadConfirmDialog
            open={uploadDialogOpen}
            file={selectedFile}
            currentPath={currentPath}
            existingFolders={folders}
            onConfirm={handleUploadConfirm}
            onCancel={handleUploadCancel}
          />
        </Paper>
      </Container>
    </FileManagerErrorBoundary>
  );
};

export default FileManagerPage;
